/**
 * MATH exercise mutations, spread into the one `assignment-editor` store next to the chapter actions
 * (RND-196 decision 13). The same two invariants hold: mutate in place (a stored exercise may carry
 * keys no schema describes), and reorder edges are no-ops.
 *
 * The prompt is not edited here: it is a `ContentBlock[]` addressed by an `{ exerciseId }` owner, so
 * the chapter block actions (`addContentBlock`, `updateTextBlock`, …) write it.
 */
import type { AssignmentEditorStore, MathExercise, MathExerciseListKey, MathExerciseListPatch, MathExerciseLists, MathExercisePatch } from './interfaces'
import type { MathPracticeDefinition } from '../types'

import { ID_PREFIX, mintId } from '@shared/lib/collections'
import {
  isRecord,
  moveWithin,
  neighbourId,
  patchOptional,
  patchRequired,
} from '@shared/lib/collections'

import { MathExerciseType } from '../enums'
import { MATH_MULTI_STEP_MAX_STEPS } from '../../../lib/assignments/math-practice-schema'

type IdPrefixForList = typeof ID_PREFIX[
  'choice' | 'pair' | 'item' | 'tick' | 'step'
]

/** Which list each type carries, and what its items are minted as. `numeric` has none. */
const LIST_OF_TYPE: Record<MathExerciseType, MathExerciseListKey | null> = {
  [MathExerciseType.MultipleChoice]: 'options',
  [MathExerciseType.Numeric]: null,
  [MathExerciseType.MatchPairs]: 'pairs',
  [MathExerciseType.Order]: 'items',
  [MathExerciseType.NumberLine]: 'ticks',
  [MathExerciseType.MultiStep]: 'steps',
}

const LIST_ID_PREFIX: Record<MathExerciseListKey, IdPrefixForList> = {
  options: ID_PREFIX.choice,
  pairs: ID_PREFIX.pair,
  items: ID_PREFIX.item,
  ticks: ID_PREFIX.tick,
  steps: ID_PREFIX.step,
}

/** Upper bounds the store enforces; lower bounds are the schema's to report, the UI's to guard. */
const LIST_MAX: Partial<Record<MathExerciseListKey, number>> = {
  steps: MATH_MULTI_STEP_MAX_STEPS,
}

/** Every key a type owns. `changeExerciseType` removes these and nothing else. */
const TYPE_FIELD_KEYS: readonly string[] = [
  'options',
  'correctOptionId',
  'answer',
  'pairs',
  'items',
  'ticks',
  'correctTickId',
  'steps',
]

// --- factories -------------------------------------------------------------

const createListItem = <K extends MathExerciseListKey>(list: K): MathExerciseLists[K] => {
  const id = mintId(LIST_ID_PREFIX[list])
  const items: { [L in MathExerciseListKey]: MathExerciseLists[L] } = {
    options: { id, text: '' },
    pairs: { id, left: '', right: '' },
    items: { id, text: '' },
    ticks: { id, label: '' },
    steps: { id, label: '', answer: '' },
  }

  return items[list]
}

type SharedExerciseKey = 'id' | 'prompt' | 'hint' | 'explanation' | 'difficulty'

/** Distributes over the union, so each member keeps its own fields. */
type ExerciseTypeFields = MathExercise extends infer E
  ? E extends MathExercise ? Omit<E, SharedExerciseKey> : never
  : never

/**
 * A type's own fields, born minimal: two items where the schema wants two, and an EMPTY answer key —
 * the validation report then asks the author to mark it rather than a default passing for a choice.
 */
const createTypeFields = (type: MathExerciseType): ExerciseTypeFields => {
  switch (type) {
    case MathExerciseType.MultipleChoice:
      return { type, options: [createListItem('options'), createListItem('options')], correctOptionId: '' }
    case MathExerciseType.Numeric:
      return { type, answer: '' }
    case MathExerciseType.MatchPairs:
      return { type, pairs: [createListItem('pairs'), createListItem('pairs')] }
    case MathExerciseType.Order:
      return { type, items: [createListItem('items'), createListItem('items')] }
    case MathExerciseType.NumberLine:
      return { type, ticks: [createListItem('ticks'), createListItem('ticks')], correctTickId: '' }
    case MathExerciseType.MultiStep:
      return { type, steps: [createListItem('steps'), createListItem('steps')] }
    default: {
      const unreachable: never = type
      return unreachable
    }
  }
}

/** Born with one empty text block, like a fresh chapter. No difficulty: the editor does not author one. */
export const createExercise = (type: MathExerciseType): MathExercise => ({
  id: mintId(ID_PREFIX.exercise),
  prompt: [{ type: 'text', value: '' }],
  ...createTypeFields(type),
}) as MathExercise

// --- reads (guarded: stored `data` is unvalidated) --------------------------

const mathDefinitionOf = (state: AssignmentEditorStore): MathPracticeDefinition | null => {
  const definition: unknown = state.workingDocument?.definition

  return isRecord(definition) ? definition as MathPracticeDefinition : null
}

/** The live `exercises` array (by reference), or `[]`. */
const exercisesOf = (state: AssignmentEditorStore): MathExercise[] => {
  const exercises: unknown = mathDefinitionOf(state)?.exercises

  return Array.isArray(exercises) ? exercises as MathExercise[] : []
}

export const findExercise = (state: AssignmentEditorStore, exerciseId: string): MathExercise | null =>
  exercisesOf(state).find(exercise => exercise?.id === exerciseId) ?? null

const exerciseIndex = (state: AssignmentEditorStore, exerciseId: string): number =>
  exercisesOf(state).findIndex(exercise => exercise?.id === exerciseId)

/** The exercise's list when its type owns `list`; `null` for any other type or a malformed list. */
const listOf = <K extends MathExerciseListKey>(
  exercise: MathExercise | null,
  list: K,
): MathExerciseLists[K][] | null => {
  if (!exercise || LIST_OF_TYPE[exercise.type] !== list) {
    return null
  }
  const record: Record<string, unknown> = exercise
  const items: unknown = record[list]

  return Array.isArray(items) ? items as MathExerciseLists[K][] : null
}

export const pruneExerciseSelection = (state: AssignmentEditorStore): void => {
  const exercises = exercisesOf(state)
  if (state.selectedExerciseId && exercises.some(exercise => exercise?.id === state.selectedExerciseId)) {
    return
  }
  state.selectedExerciseId = exercises[0]?.id ?? null
}

// --- Actions ---------------------------------------------------------------

export const exerciseActions = {
  selectExercise (this: AssignmentEditorStore, exerciseId: string | null): void {
    this.selectedExerciseId = exerciseId
  },

  setPromptTab (this: AssignmentEditorStore, payload: AssignmentEditorStore['promptTab']): void {
    this.promptTab = payload
  },

  /**
   * Appends, selects and returns the new exercise id; `null` when no document is loaded. Opens the
   * prompt on its editor: a fresh prompt has nothing to preview.
   */
  addExercise (this: AssignmentEditorStore, type: MathExerciseType = MathExerciseType.MultipleChoice): string | null {
    const definition = mathDefinitionOf(this)

    if (!definition) {
      return null
    }
    const exercises: unknown = definition.exercises

    if (!Array.isArray(exercises)) {
      // Same rule as `addChapter`: an absent list is minted, a wrong-typed one is authored data.
      if (exercises !== undefined && exercises !== null) {
        return null
      }
      definition.exercises = []
    }
    const exercise = createExercise(type)
    definition.exercises.push(exercise)
    this.selectedExerciseId = exercise.id
    this.promptTab = 'editor'

    return exercise.id
  },

  removeExercise (this: AssignmentEditorStore, exerciseId: string): void {
    const exercises = exercisesOf(this)
    const index = exerciseIndex(this, exerciseId)

    if (index < 0) {
      return
    }
    exercises.splice(index, 1)

    if (this.selectedExerciseId === exerciseId) {
      this.selectedExerciseId = neighbourId(exercises, index)
    }
  },

  moveExerciseUp (this: AssignmentEditorStore, exerciseId: string): void {
    moveWithin(exercisesOf(this), exerciseIndex(this, exerciseId), -1)
  },

  moveExerciseDown (this: AssignmentEditorStore, exerciseId: string): void {
    moveWithin(exercisesOf(this), exerciseIndex(this, exerciseId), 1)
  },

  /**
   * Keeps what every type shares — id, prompt, hint, explanation, difficulty, and any key no schema
   * describes — and replaces the type's own fields with a fresh set. The same type is a no-op.
   */
  changeExerciseType (this: AssignmentEditorStore, exerciseId: string, type: MathExerciseType): void {
    const exercise = findExercise(this, exerciseId)

    if (!exercise || exercise.type === type) {
      return
    }
    const target: Record<string, unknown> = exercise

    TYPE_FIELD_KEYS.forEach((key) => {
      Reflect.deleteProperty(target, key)
    })
    Object.assign(target, createTypeFields(type))
  },

  /** The fields every type shares. An emptied hint or explanation drops the key. */
  updateExercise (this: AssignmentEditorStore, exerciseId: string, patch: MathExercisePatch): void {
    const exercise = findExercise(this, exerciseId)

    if (!exercise) {
      return
    }
    patchOptional(exercise, 'hint', patch.hint === '' ? null : patch.hint)
    patchOptional(exercise, 'explanation', patch.explanation === '' ? null : patch.explanation)
    patchRequired(exercise, 'difficulty', patch.difficulty)
  },

  /** The numeric answer. Any other type is a no-op. */
  setExerciseAnswer (this: AssignmentEditorStore, exerciseId: string, answer: string): void {
    const exercise = findExercise(this, exerciseId)

    if (exercise?.type !== MathExerciseType.Numeric) {
      return
    }
    exercise.answer = answer
  },

  /** Keys the answer by id: the option (multiple choice) or the tick (number line). An id the list
   *  does not hold is refused. */
  setExerciseCorrectId (this: AssignmentEditorStore, exerciseId: string, itemId: string): void {
    const exercise = findExercise(this, exerciseId)

    if (exercise?.type === MathExerciseType.MultipleChoice) {
      if (listOf(exercise, 'options')?.some(option => option?.id === itemId)) {
        exercise.correctOptionId = itemId
      }
      return
    }
    if (exercise?.type === MathExerciseType.NumberLine) {
      if (listOf(exercise, 'ticks')?.some(tick => tick?.id === itemId)) {
        exercise.correctTickId = itemId
      }
    }
  },

  /** Appends an item to the type's own list and returns its id; `null` for a list the type does not
   *  own, or one already at its maximum (multi-step has four steps at most). */
  addExerciseListItem (this: AssignmentEditorStore, exerciseId: string, list: MathExerciseListKey): string | null {
    const items = listOf(findExercise(this, exerciseId), list)
    const max = LIST_MAX[list]

    if (!items || (max !== undefined && items.length >= max)) {
      return null
    }
    const item = createListItem(list)
    items.push(item)

    return item.id
  },

  /** Removing the keyed option or tick clears the key, so the report asks for a new one. */
  removeExerciseListItem (
    this: AssignmentEditorStore,
    exerciseId: string,
    list: MathExerciseListKey,
    itemId: string,
  ): void {
    const exercise = findExercise(this, exerciseId)
    const items = listOf(exercise, list)
    const index = items?.findIndex(item => item?.id === itemId) ?? -1

    if (!items || index < 0) {
      return
    }
    items.splice(index, 1)

    if (exercise?.type === MathExerciseType.MultipleChoice && exercise.correctOptionId === itemId) {
      exercise.correctOptionId = ''
    }
    if (exercise?.type === MathExerciseType.NumberLine && exercise.correctTickId === itemId) {
      exercise.correctTickId = ''
    }
  },

  /** `delta` of `-1` moves up, `1` down. For `items` this IS the answer key: the stored order is the
   *  correct one. */
  moveExerciseListItem (
    this: AssignmentEditorStore,
    exerciseId: string,
    list: MathExerciseListKey,
    itemId: string,
    delta: -1 | 1,
  ): void {
    const items = listOf(findExercise(this, exerciseId), list)

    if (!items) {
      return
    }
    moveWithin(items, items.findIndex(item => item?.id === itemId), delta)
  },

  updateExerciseListItem<K extends MathExerciseListKey> (
    this: AssignmentEditorStore,
    exerciseId: string,
    list: K,
    itemId: string,
    patch: MathExerciseListPatch<K>,
  ): void {
    const item: unknown = listOf(findExercise(this, exerciseId), list)?.find(entry => entry?.id === itemId)

    if (!isRecord(item)) {
      return
    }
    Object.entries(patch).forEach(([key, value]) => {
      if (typeof value === 'string' && key !== 'id') {
        item[key] = value
      }
    })
  },
}
